import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";

import LinkModal from "@/components/LinkModal";

import CountdownTimer from "../CountDownTimer";
import { useStore } from "../../store";

import styles from "./index.module.scss";
import { Discount } from "../../types/discounts";

const { mozartSale } = window;

function getGermanTime(date: Date | undefined = undefined) {
	date = new Date(date ?? Date.now());

	return new Date(date.toLocaleString("en-US", { timeZone: "Europe/Berlin" }))
}

interface SaleProps {
	openSaveModal: () => void
}

export default function Sale({ openSaveModal }: SaleProps) {
	const { isSale, discounts, discount, isLastDay } = useSale();

	const discountElements = useMemo(() => discounts.map((item) => (
		<div
			className={classNames(styles.discount, {
				[styles.active]: item.discount === discount
			})}
			key={item.discount}
		>
			<span className={styles.checkbox}>{item.text}</span>
			<b className={styles.value}>{item.discount} € Rabatt</b>
		</div>
	)), [discount]);

	if (!isSale) {
		return null;
	}

	const text = isLastDay ? "Aktion endet" : "Rabatte sinken";

	return (
		<div className={styles.sale}>

			<header className={styles.header}>
				<div className={styles.text}>
					<span>Traumhafte Rabatte</span>
					<span className={styles.accent}>im Mozart {mozartSale.name}</span>
				</div>

				{<CountdownTimer isSale={isSale} />}

			</header>

			<div className={styles.discounts}>
				{discountElements}
			</div>

			<footer className={styles.footer}>
				<h6>Die {text} heute um 00:00 Uhr. </h6>
				<div className={styles.text}>
					Noch nicht bereit, heute zu bestellen? <a className={styles.underline}
					                                          onClick={() => openSaveModal()}>Speichere
					Deinen Entwurf</a> und sichere Dir die heutigen Rabatte
					bis zum {Intl.DateTimeFormat('de-DE', {
					day: '2-digit',
					month: 'long'
				}).format(mozartSale.couponsExpire)}.&nbsp;
					<LinkModal
						linkText="Aktionshinweise"
						modalTitle="Aktionshinweise"
						modalClassName={styles.infoModal}
					>
						<h3>Hinweise zum Mozart {mozartSale.name}:</h3>

						<br />

						<p>Heute bis 23:59 Uhr gelten folgende Rabatte, gestaffelt nach Bestellwert:</p>

						<br />

						<ul>
							{discounts.map((discount) => (
								<li key={discount.discount}>
									{discount.text_long ?? discount.text} {"->"} <b>{discount.discount} € Rabatt</b>
								</li>
							))}
						</ul>

						<br />

						<h4>Weitere Hinweise:</h4>
						<ul>
							<li>Der {mozartSale.name} läuft bis zum {mozartSale.endDate} um 23:59:59 Uhr.</li>
							<li>Du erhältst die höchsten Rabatte des bisherigen Jahres.</li>
							<li>Die Rabatte reduzieren sich jede Nacht um 00:00 Uhr.</li>
							<li>Deine Bestellung muss ein Boxspringbett enthalten, damit der Rabatt angewendet werden
								kann.
							</li>
							<li>Der Rabatt wird automatisch an der Kasse abgezogen.</li>
							<li>
								Wenn Du noch nicht bereit bist, kannst Du Dir die heutigen Rabatte sichern, indem Du
								Dein Bett
								als <b>Entwurf speicherst</b> (das geht im letzten Schritt des Konfigurators)
								oder <b>Stoffmuster</b> bestellst.
								Du erhältst die Rabattcodes per E-Mail, die bis
								zum {Intl.DateTimeFormat("de-DE", {
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
							}).format(mozartSale.couponsExpire)} gültig
								sind.
							</li>
							<li>Die Rabatte sind nicht mit anderen Rabatten kombinierbar.</li>
						</ul>
					</LinkModal>
				</div>
			</footer>
		</div>
	);
}

interface UseSale {
	isSale: boolean
	sale: typeof mozartSale
	discount: number
	discounts: Discount[]
	isLastDay: boolean
}

let zipCache: number | undefined = undefined;
let discountCache: Discount[] = [];

function getDiscounts(zip = -1) {

	const { discounts } = mozartSale;

	const formatter = Intl.NumberFormat("de-DE");

	discountCache = discounts.map((item) => {
		const newItem = { ...item };

		const text = `${newItem.from ? "über" : "bis"} ${formatter.format(newItem.from ?? newItem.limit)} €`;

		let text_long: string | null = null;

		if (newItem.from && newItem.limit && newItem.limit < 999999) {
			text_long = `ab ${formatter.format(newItem.from)} bis ${formatter.format(newItem.limit)} €`;
		}

		return {
			...newItem,
			discount: newItem.amount,
			text,
			text_long,
		};
	});

	return discountCache;
}

export function useSale(): UseSale {
	const { prices: { price }, zip } = useStore();

	const { end, start } = mozartSale ?? { end: undefined, start: undefined },
		now = getGermanTime().getTime() / 1000;
	const isSale = now >= start && now < end;

	const [discount, setDiscount] = useState(0);
	const discounts = useMemo(() => isSale ? getDiscounts(zip) : [], [zip]);

	const isLastDay = end - now < 86400;


	useEffect(() => {
		if (!isSale || !price) return;

		for (const discount of discounts) {
			if (price < discount.limit) {
				setDiscount(discount.discount);
				break;
			}
		}
	}, [price]);

	return { isSale, sale: mozartSale, discount, discounts, isLastDay };
}

const lowPriceDiscounts = Object.entries(window.mozartSale?.lowPriceDiscounts ?? {}) as unknown as [number, number][];

export function calculateLowPrice(price: number) {

	if (lowPriceDiscounts.length === 0) {
		return price;
	}

	// price -= 100;

	// const days = Math.floor((Date.now() / 1000 - window.mozartSale.start) / (60 * 60 * 24));

	for (let [discount, limit] of lowPriceDiscounts) {
		if (price > limit) {
			continue;
		}

		// discount = Number(discount) - days;

		return price - discount;
	}

	return 0;
}
